/**
 * Modal module
 * Dynamic modal dialogs with HTMX support
 */

/**
 * Active modals stack
 */
const activeModals = [];

/**
 * Default modal options
 */
const defaultModalOptions = {
    size: 'medium',
    closable: true,
    backdrop: true,
    keyboard: true,
    focus: true,
    scroll: false
};

/**
 * Modal sizes
 */
const sizes = {
    small: 'max-w-md',
    medium: 'max-w-lg',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl',
    full: 'max-w-full'
};

/**
 * Create modal element
 * @param {string} content - Modal content HTML
 * @param {Object} options - Modal options
 * @returns {Object} Modal elements {backdrop, modal}
 */
function createModal(content, options = {}) {
    const opts = { ...defaultModalOptions, ...options };
    
    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'fixed inset-0 bg-gray-500 bg-gray-500/75 transition-opacity z-40';
    backdrop.setAttribute('aria-hidden', 'true');
    
    // Create modal container
    const modalContainer = document.createElement('div');
    modalContainer.className = 'fixed inset-0 z-50 overflow-y-auto';
    modalContainer.setAttribute('role', 'dialog');
    modalContainer.setAttribute('aria-modal', 'true');
    
    // Create modal wrapper
    const modalWrapper = document.createElement('div');
    modalWrapper.className = 'flex min-h-full items-center justify-center p-4 sm:p-0';
    
    // Create modal
    const modal = document.createElement('div');
    modal.className = `relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full ${sizes[opts.size] || sizes.medium}`;
    
    // Parse content structure
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Check if content has modal structure
    const hasHeader = tempDiv.querySelector('.modal-header');
    const hasBody = tempDiv.querySelector('.modal-body');
    const hasFooter = tempDiv.querySelector('.modal-footer');
    
    if (hasHeader || hasBody || hasFooter) {
        // Use provided structure
        modal.innerHTML = content;
    } else {
        // Wrap in default structure
        modal.innerHTML = `
            <div class="modal-body">
                ${content}
            </div>
        `;
    }
    
    // Add close button if closable and no header
    if (opts.closable && !hasHeader) {
        const closeBtn = document.createElement('button');
        closeBtn.className = 'absolute top-4 right-4 text-gray-400 hover:text-gray-500';
        closeBtn.innerHTML = `
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        `;
        modal.appendChild(closeBtn);
        closeBtn.addEventListener('click', () => closeModal(modalContainer));
    }
    
    // Assemble modal
    modalWrapper.appendChild(modal);
    modalContainer.appendChild(modalWrapper);
    
    return { backdrop, modalContainer, modal, options: opts };
}

/**
 * Show modal
 * @param {Object} modalData - Modal data from createModal
 */
function showModal(modalData) {
    const { backdrop, modalContainer, modal, options } = modalData;
    
    // Add to active modals
    activeModals.push(modalData);
    
    // Add backdrop click handler
    if (options.backdrop && options.closable) {
        backdrop.addEventListener('click', () => closeModal(modalContainer));
        modalContainer.addEventListener('click', (e) => {
            if (e.target === modalContainer || e.target === modalContainer.firstElementChild) {
                closeModal(modalContainer);
            }
        });
    }
    
    // Add keyboard handler
    if (options.keyboard) {
        const handleKeydown = (e) => {
            if (e.key === 'Escape' && options.closable) {
                closeModal(modalContainer);
            }
        };
        modalContainer.addEventListener('keydown', handleKeydown);
        modalData.keyHandler = handleKeydown;
    }
    
    // Prevent body scroll
    if (!options.scroll) {
        document.body.style.overflow = 'hidden';
    }
    
    // Add to DOM
    document.body.appendChild(backdrop);
    document.body.appendChild(modalContainer);
    
    // Animate in
    requestAnimationFrame(() => {
        backdrop.style.opacity = '0';
        modal.style.opacity = '0';
        modal.style.transform = 'scale(0.95)';
        
        requestAnimationFrame(() => {
            backdrop.style.opacity = '1';
            modal.style.opacity = '1';
            modal.style.transform = 'scale(1)';
        });
    });
    
    // Focus management
    if (options.focus) {
        const focusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable) {
            focusable.focus();
        }
    }
    
    // Trap focus
    trapFocus(modalContainer);
}

/**
 * Close modal
 * @param {HTMLElement} modalContainer - Modal container element
 */
function closeModal(modalContainer) {
    const modalIndex = activeModals.findIndex(m => m.modalContainer === modalContainer);
    if (modalIndex === -1) return;
    
    const modalData = activeModals[modalIndex];
    const { backdrop, modal, options } = modalData;
    
    // Animate out
    backdrop.style.opacity = '0';
    modal.style.opacity = '0';
    modal.style.transform = 'scale(0.95)';
    
    setTimeout(() => {
        // Remove from DOM
        backdrop.remove();
        modalContainer.remove();
        
        // Remove from active modals
        activeModals.splice(modalIndex, 1);
        
        // Restore body scroll if no more modals
        if (activeModals.length === 0 && !options.scroll) {
            document.body.style.overflow = '';
        }
        
        // Remove keyboard handler
        if (modalData.keyHandler) {
            document.removeEventListener('keydown', modalData.keyHandler);
        }
        
        // Trigger close event
        if (options.onClose) {
            options.onClose();
        }
    }, 300);
}

/**
 * Trap focus within modal
 * @param {HTMLElement} element - Container element
 */
function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    element.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        
        if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
                e.preventDefault();
                lastFocusable.focus();
            }
        } else {
            if (document.activeElement === lastFocusable) {
                e.preventDefault();
                firstFocusable.focus();
            }
        }
    });
}

/**
 * Open modal with content
 * @param {string} content - Modal content HTML
 * @param {Object} options - Modal options
 * @returns {Object} Modal API
 */
export function open(content, options = {}) {
    const modalData = createModal(content, options);
    showModal(modalData);
    
    return {
        close: () => closeModal(modalData.modalContainer),
        update: (newContent) => {
            modalData.modal.innerHTML = newContent;
        },
        element: modalData.modal
    };
}

/**
 * Open modal from URL (HTMX-friendly)
 * @param {string} url - URL to load content from
 * @param {Object} options - Modal options
 * @returns {Promise<Object>} Modal API
 */
export async function openUrl(url, options = {}) {
    // Show loading modal
    const loadingModal = open(`
        <div class="p-8 text-center">
            <div class="inline-flex items-center">
                <svg class="animate-spin h-8 w-8 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="ml-2 text-gray-600">Loading...</span>
            </div>
        </div>
    `, { ...options, closable: false });
    
    try {
        const response = await fetch(url, {
            headers: {
                'HX-Request': 'true',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const content = await response.text();
        loadingModal.update(content);
        
        // Re-enable closable if originally requested
        if (options.closable !== false) {
            const closeBtn = document.createElement('button');
            closeBtn.className = 'absolute top-4 right-4 text-gray-400 hover:text-gray-500';
            closeBtn.innerHTML = `
                <span class="sr-only">Close</span>
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            `;
            loadingModal.element.appendChild(closeBtn);
            closeBtn.addEventListener('click', () => loadingModal.close());
        }
        
        // Process HTMX content
        if (window.htmx) {
            window.htmx.process(loadingModal.element);
        }
        
        return loadingModal;
    } catch (error) {
        loadingModal.update(`
            <div class="p-8">
                <div class="flex items-center mb-4 text-red-600">
                    <svg class="h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 class="text-lg font-medium">Error loading content</h3>
                </div>
                <p class="text-gray-600">${error.message}</p>
                <div class="mt-4">
                    <button onclick="this.closest('.fixed').remove()" class="btn-secondary">Close</button>
                </div>
            </div>
        `);
        
        throw error;
    }
}

/**
 * Close all modals
 */
export function closeAll() {
    [...activeModals].reverse().forEach(modalData => {
        closeModal(modalData.modalContainer);
    });
}

/**
 * Get active modal count
 * @returns {number} Number of active modals
 */
export function getActiveCount() {
    return activeModals.length;
}

/**
 * Alert modal
 * @param {string} message - Alert message
 * @param {Object} options - Modal options
 * @returns {Promise} Resolves when modal is closed
 */
export function alert(message, options = {}) {
    return new Promise((resolve) => {
        const {
            title = 'Alert',
            type = 'info',
            confirmText = 'OK'
        } = options;
        
        const icons = {
            success: `<svg class="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>`,
            error: `<svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>`,
            warning: `<svg class="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>`,
            info: `<svg class="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>`
        };
        
        const modal = open(`
            <div class="p-6">
                <div class="flex items-start">
                    <div class="shrink-0">
                        ${icons[type] || icons.info}
                    </div>
                    <div class="ml-3">
                        <h3 class="text-lg font-medium text-gray-900 mb-2">${title}</h3>
                        <p class="text-sm text-gray-500">${message}</p>
                    </div>
                </div>
            </div>
            <div class="bg-gray-50 px-6 py-3 flex justify-end">
                <button type="button" class="btn-primary alert-confirm">${confirmText}</button>
            </div>
        `, { ...options, size: 'small' });
        
        const confirmBtn = modal.element.querySelector('.alert-confirm');
        confirmBtn.addEventListener('click', () => {
            modal.close();
            resolve();
        });
        
        confirmBtn.focus();
    });
}

/**
 * Initialize HTMX modal triggers
 */
export function init() {
    // Handle modal triggers
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-modal-trigger]');
        if (!trigger) return;
        
        e.preventDefault();
        const url = trigger.getAttribute('href') || trigger.getAttribute('data-modal-url');
        const options = {
            size: trigger.getAttribute('data-modal-size') || 'medium',
            closable: trigger.getAttribute('data-modal-closable') !== 'false'
        };
        
        openUrl(url, options);
    });
    
    // Handle HTMX modal responses
    document.addEventListener('htmx:beforeSwap', (e) => {
        if (e.detail.xhr.getResponseHeader('HX-Modal')) {
            e.preventDefault();
            open(e.detail.xhr.responseText);
        }
    });
}